/* eslint-disable react/prop-types */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState, useMemo, useCallback } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import MDInput from "../../../components/MDInput";
import toast from "react-hot-toast";
import subDays from "date-fns/subDays";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from "papaparse";

function CardTransactions({ embedded = false, tabsSlot = null } = {}) {
  const { cardTransactionsReportsColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Filter state
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterIsQr, setFilterIsQr] = useState("");
  const [lastMonthDateRange, setLastMonthDateRange] = useState([new Date().toISOString().slice(0, 10),  new Date().toISOString().slice(0, 10)]);
  const [refundingId, setRefundingId] = useState(null);
  const [refundDialog, setRefundDialog] = useState({ open: false, transaction: null, reason: "Customer requested refund" });
  // const [fromDate, setFromDate]= useState(lastMonthDateRange[0]);
  // const [toDate, setToDate ]= useState(lastMonthDateRange[1]);
  const api = useApi();
  const formatDate = (date) => {
    const isoDate = new Date(date).toISOString();
    const year = isoDate.slice(0, 4);
    const month = isoDate.slice(5, 7);
    const day = isoDate.slice(8, 10);
    return `${month}/${day}/${year}`;
  };
  const getTransactions = (lastMonthDateRange) => {
    if(lastMonthDateRange == null) return;
    setLastMonthDateRange(lastMonthDateRange);
    const data = {
      startDate : formatDate(lastMonthDateRange[0]),
      endDate : formatDate(lastMonthDateRange[1])
    }
    api.post("transaction-details", data)
      .then((response) => {
        setTransactions(response.data.data);
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.", { id: "PermissionError" });
          return;
        }
        // No matching transactions is surfaced by the table's own empty state, not a toast
        setTransactions([]);
      })
  }

  useEffect(() => {
    getTransactions(lastMonthDateRange);
  },[]);

  useEffect(() => {
    let result = [...transactions];
    const search = filterSearch.trim().toLowerCase();
    if (search) {
      result = result.filter(t => {
        const merchantTxnId = t.paymentType === "REF" ? t.wallet?.walletTransactionId : t.referenceId;
        const searchableValues = [
          t.merchant?.cardAcceptorCode,
          t.merchant?.cardAcceptorName,
          t.amountInCents,
          t.amountInCents != null ? (t.amountInCents / 100).toFixed(2) : null,
          t.processorTransactionId,
          merchantTxnId,
          t.paymentId,
          t.sessionId,
          t.refundReference,
        ];
        return searchableValues.some(v => v != null && String(v).toLowerCase().includes(search));
      });
    }
    if (filterStatus) {
      result = result.filter(t => t.status === filterStatus);
    }
    if (filterIsQr !== "") {
      result = result.filter(t => String(t.isQrPayment) === filterIsQr);
    }
    setFilteredTransactions(result);
  }, [transactions, filterSearch, filterStatus, filterIsQr]);

  const resetFilters = () => {
    setFilterSearch("");
    setFilterStatus("");
    setFilterIsQr("");
  };

  const handleDownloadCSV = () => {
    if (filteredTransactions.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const headers = cardTransactionsReportsColumns.map((c) => c.Header);
    const values = filteredTransactions.map((row) =>
      cardTransactionsReportsColumns.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    const csv = Papa.unparse([headers, ...values], { quotes: true });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TransactionDetails_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const executeRefund = useCallback((transaction, reason) => {
    setRefundingId(transaction.paymentId);
    api.post(`payments/${transaction.paymentId}/refund`, {
      amountInCents: transaction.amountInCents,
      reason,
    })
      .then((response) => {
        const refundData = response.data?.data;
        const status = refundData?.status ?? "REFUNDED";
        toast.success(`Refund successful. Status: ${status}`);
        getTransactions(lastMonthDateRange);
      })
      .catch((error) => {
        const msg = error.response?.data?.error?.message || "Failed to process refund. Please try again.";
        toast.error(msg);
      })
      .finally(() => {
        setRefundingId(null);
      });
  }, [api, lastMonthDateRange]);

  const handleRefundClick = useCallback((transaction) => {
    setRefundDialog({ open: true, transaction, reason: "Customer requested refund" });
  }, []);

  const handleRefundConfirm = () => {
    const { transaction, reason } = refundDialog;
    setRefundDialog({ open: false, transaction: null, reason: "Customer requested refund" });
    executeRefund(transaction, reason);
  };

  const handleRefundCancel = () => {
    setRefundDialog({ open: false, transaction: null, reason: "Customer requested refund" });
  };

  const tableColumns = useMemo(() => [
    ...cardTransactionsReportsColumns,
    {
      Header: "Action",
      id: "refund-action",
      disableSortBy: true,
      /* eslint-disable react/prop-types */
      Cell: ({ row }) => row.original.canRefund ? (
        <MDButton
          variant="contained"
          color="error"
          size="small"
          onClick={() => handleRefundClick(row.original)}
          disabled={refundingId === row.original.paymentId}
          style={{ fontSize: "0.75rem", padding: "4px 12px", whiteSpace: "nowrap" }}
        >
          {refundingId === row.original.paymentId ? "Processing..." : "Refund"}
        </MDButton>
      ) : null,
      /* eslint-enable react/prop-types */
    },
  ], [cardTransactionsReportsColumns, handleRefundClick, refundingId]);

  const content = (
    <>
      {/* Refund Confirmation Dialog */}
      <Dialog open={refundDialog.open} onClose={handleRefundCancel} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "12px", p: 1 } }}
      >
        <DialogTitle sx={{ pb: 0, fontWeight: 700, fontSize: "1.1rem" }}>
          Confirm Refund
        </DialogTitle>
        <Divider sx={{ mt: 1.5, mb: 0 }} />
        <DialogContent sx={{ pt: 2 }}>
          <MDBox mb={2} p={1.5} sx={{ background: "#f5f5f5", borderRadius: "8px" }}>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
              Refund Amount
            </MDTypography>
            <MDTypography variant="h5" fontWeight="bold" color="error">
              {refundDialog.transaction
                ? `${(refundDialog.transaction.amountInCents / 100).toFixed(2)} ${refundDialog.transaction.currency}`
                : ""}
            </MDTypography>
          </MDBox>
          <TextField
            label="Reason"
            multiline
            minRows={3}
            fullWidth
            variant="outlined"
            value={refundDialog.reason}
            onChange={(e) => setRefundDialog((prev) => ({ ...prev, reason: e.target.value }))}
            InputProps={{ style: { fontSize: "0.875rem" } }}
            InputLabelProps={{ style: { fontSize: "0.875rem" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <MDButton variant="outlined" color="secondary" onClick={handleRefundCancel} fullWidth>
            Cancel
          </MDButton>
          <MDButton
            variant="contained"
            color="error"
            onClick={handleRefundConfirm}
            disabled={!refundDialog.reason?.trim()}
            fullWidth
          >
            Refund
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid item xs={4}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    Reports
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="regular" color="secondary">
                    {filteredTransactions.length} Transactions
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="center" className="no-print">
                <DateRangePicker disabledDate={afterToday()} ranges={predefinedRanges} onChange={(date) => getTransactions(date)} format="d MMMM yyyy" value={Object.keys(lastMonthDateRange).length !== 0 ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] : []} />
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="flex-end">
                <MDButton variant="gradient" color="info" onClick={handleDownloadCSV}>
                  Export CSV
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
          {tabsSlot && (
            <Grid item xs={12}>
              {tabsSlot}
            </Grid>
          )}
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    label="Search"
                    placeholder="Merchant ID, Name, Amount, Processor/Merchant TXN Id..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" }, "& .MuiSelect-select": { fontSize: "0.8rem" } }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>All</MenuItem>
                    <MenuItem value="INITIATED" style={{ fontSize: "0.8rem" }}>INITIATED</MenuItem>
                    <MenuItem value="AUTHORIZED" style={{ fontSize: "0.8rem" }}>APPROVED</MenuItem>
                    <MenuItem value="FAILED" style={{ fontSize: "0.8rem" }}>FAILED</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={true}>
                  <TextField
                    select
                    label="QR Payment"
                    value={filterIsQr}
                    onChange={(e) => setFilterIsQr(e.target.value)}
                    fullWidth size="small" variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" }, "& .MuiSelect-select": { fontSize: "0.8rem" } }}
                  >
                    <MenuItem value="" style={{ fontSize: "0.8rem" }}>All</MenuItem>
                    <MenuItem value="true" style={{ fontSize: "0.8rem" }}>Yes</MenuItem>
                    <MenuItem value="false" style={{ fontSize: "0.8rem" }}>No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg="auto">
                  <MDButton variant="outlined" color="secondary" onClick={resetFilters} size="small" style={{ fontSize: "0.75rem", padding: "5px 16px", whiteSpace: "nowrap" }}>
                    Reset
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
            <DataTable
              table={{ columns : tableColumns, rows : filteredTransactions }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              entriesPerPagePosition="bottom"
              canSearch={false}
            />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
      <Footer />
    </DashboardLayout>
  );
}

export default CardTransactions;
